import * as Select from "@radix-ui/react-select"
import { IconChevronDown } from "@tabler/icons-react"
import { useParams } from "@tanstack/react-router"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { ALL_APP_LANGUAGES, resolveAppLanguage } from "@/locales/detectBrowserLanguage"
import { useSessionConfig } from "@/services/dexie"
import type { InstacarAppLanguage } from "@/utils/types"

import { applyLanguage } from "../locales/i18n"
import { persistLanguage } from "../locales/persistedLanguage"
import { FlagIcon } from "./icons/FlagIcon"

const FLAG_WIDTH = 24

/**
 * Language switcher for the layout header. Whether it shows at all is the caller's decision,
 * which languages it offers is derived from the route.
 */
export function LocalesSelect() {
  const { sessionId } = useParams({ strict: false })

  return sessionId ? <SessionLocalesSelect sessionId={sessionId} /> : <SessionlessLocalesSelect />
}

type LocalesSelectViewProps = {
  languages: readonly InstacarAppLanguage[]
  activeLanguage: InstacarAppLanguage
  onSelect: (language: InstacarAppLanguage) => void
}

function LocalesSelectView(props: LocalesSelectViewProps) {
  const { languages, activeLanguage, onSelect } = props

  const otherLanguages = useMemo(
    () => languages.filter((language) => language !== activeLanguage),
    [languages, activeLanguage],
  )

  // Nothing to switch between
  if (languages.length < 2) {
    return null
  }

  function onValueChange(value: string) {
    const language = languages.find((candidate) => candidate === value)

    if (language) {
      onSelect(language)
    }
  }

  return (
    <Select.Root value={activeLanguage} onValueChange={onValueChange}>
      <Select.Trigger
        aria-label="Language"
        className="flex shrink-0 cursor-pointer items-center gap-1 focus:outline-none"
      >
        <FlagIcon language={activeLanguage} width={FLAG_WIDTH} />

        <Select.Icon>
          <IconChevronDown className="text-ic-heading size-5" />
        </Select.Icon>
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          position="popper"
          side="bottom"
          align="end"
          sideOffset={8}
          className="ic-z-popover rounded-2xl bg-white p-2 shadow-[3px_3px_16px_#00000029]"
        >
          <Select.Viewport className="flex flex-col gap-2">
            {otherLanguages.map((language) => (
              <Select.Item
                key={language}
                value={language}
                className="flex cursor-pointer items-center gap-2 px-1 focus:outline-none"
              >
                <FlagIcon language={language} width={FLAG_WIDTH} />

                <Select.ItemText>
                  <span className="font-ic-heading text-ic-heading text-base font-bold">{language.toUpperCase()}</span>
                </Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  )
}

type SessionLocalesSelectProps = {
  sessionId: string
}

/** Offers the customer's languages and persists the pick for that customer. */
function SessionLocalesSelect(props: SessionLocalesSelectProps) {
  const { sessionId } = props
  const { data: config } = useSessionConfig(sessionId)
  const { i18n } = useTranslation()

  const languages = config?.languages
  const activeLanguageCode = i18n.language

  // The first configured language is the app default, so it also covers an unexpected i18next language
  const activeLanguage = useMemo(() => {
    if (!languages) return

    return languages.find((language) => language === activeLanguageCode) ?? languages[0]
  }, [languages, activeLanguageCode])

  if (!config || !languages || !activeLanguage) {
    return null
  }

  async function selectLanguage(language: InstacarAppLanguage) {
    if (!config) return

    await applyLanguage(language, config.organizationHumanReadableId)
    persistLanguage(language, config.customerId)
  }

  return <LocalesSelectView languages={languages} activeLanguage={activeLanguage} onSelect={selectLanguage} />
}

/** No customer to ask, so every language the app ships is offered and the pick is stored globally. */
function SessionlessLocalesSelect() {
  const { i18n } = useTranslation()

  async function selectLanguage(language: InstacarAppLanguage) {
    // No organization bundle — screens without a session always show the default translations
    await applyLanguage(language)
    persistLanguage(language)
  }

  return (
    <LocalesSelectView
      languages={ALL_APP_LANGUAGES}
      activeLanguage={resolveAppLanguage(i18n.language)}
      onSelect={selectLanguage}
    />
  )
}
